import React, { useState, useEffect } from "react";
import {
  subscribeToUserNotifications,
  updateNotificationAsRead,
} from "config/firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import NotifImg from "../../assets/images/notif.png";

interface NotificationItem {
  avatar: string;
  username: string;
  responderName: string;
  id: string;
  message: string;
  time: string;
  isNew: boolean;
}

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 1) return "0 min";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""}`;

  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? "s" : ""}`;
};

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) {
    return `${parts[0][0]}${parts[0].slice(-1)}`.toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        const unsubscribeNotifications = subscribeToUserNotifications(
          user.uid,
          (fetchedNotifications) => {
            const mapped = fetchedNotifications.map((notif: any) => ({
              avatar: "",
              id: notif.id,
              username: notif.responderName || "Anonymous",
              responderName: notif.responderName || "Anonymous",
              message: notif.message || "",
              time: notif.createdAt?.toDate?.().toISOString() || "",
              isNew: !notif.IsRead,
            }));
            mapped.sort(
              (a: NotificationItem, b: NotificationItem) =>
                new Date(b.time).getTime() - new Date(a.time).getTime()
            );
            setNotifications(mapped);
          }
        );
        return () => unsubscribeNotifications();
      } else {
        setNotifications([]);
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSelectNotification = async (index: number) => {
    setSelectedIndex(index);

    const notification = notifications[index];
    if (notification.isNew && userId) {
      await updateNotificationAsRead(notification.id, userId);

      const updated = [...notifications];
      updated[index] = { ...notification, isNew: false };
      setNotifications(updated);
    }
  };

  const selectedNotification =
    selectedIndex !== null ? notifications[selectedIndex] : null;

  return (
    <div>
      <h1 className="text-2xl font-bold text-grease-100 mb-6">Notifications</h1>

      <div className="flex gap-6" style={{ minHeight: "calc(100vh - 220px)" }}>
        {/* Left - Notification List */}
        <div className="w-[400px] flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {notifications.length > 0 ? (
              <div className="divide-y divide-gray-100 max-h-[calc(100vh-260px)] overflow-y-auto">
                {notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    onClick={() => handleSelectNotification(index)}
                    className={`flex items-center gap-3 px-5 py-4 cursor-pointer transition-colors ${
                      selectedIndex === index
                        ? "border-l-2 border-l-light-yellow bg-yellow-50/50"
                        : "border-l-2 border-l-transparent hover:bg-gray-50"
                    }`}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-600 text-sm font-bold">
                        {getInitials(notification.responderName)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-grease-100 truncate">
                        {notification.responderName}
                      </p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {notification.responderName} has answered your...
                      </p>
                    </div>

                    {/* Time & Arrow */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        {notification.time ? formatTimeAgo(notification.time) : ""}
                      </span>
                      <i className="st-arrow-right text-xs text-gray-300"></i>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-60">
                <p className="text-gray-400 text-sm">No notifications</p>
              </div>
            )}
          </div>
        </div>

        {/* Right - Notification Detail */}
        <div className="flex-1">
          {selectedNotification ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              {/* Header with avatar and name */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 text-gray-600 text-sm font-bold">
                    {getInitials(selectedNotification.responderName)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-grease-100">
                      {selectedNotification.responderName}
                    </p>
                    {/* <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="text-xs text-green-500">Online</span>
                    </div> */}
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600 p-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <circle cx="10" cy="4" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="10" cy="16" r="1.5" />
                  </svg>
                </button>
              </div>

              {/* Message */}
              <div className="mt-6">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {selectedNotification.message}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 flex flex-col items-center justify-center h-full">
              <img src={NotifImg} alt="No notification selected" />
              <p className="text-grease-100 font-semibold text-base mt-4">You're all caught up</p>
              <p className="text-gray-400 text-sm mt-1 text-center">
                Choose a notification to see more<br />details here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
